import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { usePurchaseDetails } from '@/hooks/usePurchaseDetails';
import { useSequencePurchase } from '@/hooks/useSequencePurchase';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Download, Loader2, AlertCircle, User, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import DownloadErrorNotification from '@/components/error/DownloadErrorNotification';
import NetworkStatusMonitor from '@/components/network/NetworkStatusMonitor';
import { NetworkRecoveryManager } from '@/lib/networkRecoveryManager';

export interface GenericDownloadHandlerProps {
  sequenceId: string;
  onDownloadComplete?: () => void;
  onError?: (error: string) => void;
  autoStartDownload?: boolean;
  
  // Configuration for different flow types
  flowType: 'paid' | 'free';
  
  // For paid sequences - purchase validation
  requirePurchaseValidation?: boolean;
  
  // For free sequences - direct sequence data
  sequence?: {
    id: string;
    title: string;
    artist: string;
    sequence_price?: number;
  };
  
  // Modal flow configuration
  showDonationModal?: boolean;
  showAddedToDisplayModal?: boolean;
  showConfirmationPage?: boolean;
}

type HandlerState = 
  | 'initializing'
  | 'validating-access' 
  | 'auth-required'
  | 'ready-to-download'
  | 'download-starting'
  | 'downloading'
  | 'download-complete'
  | 'showing-modals'
  | 'completed'
  | 'error';

/**
 * GenericDownloadHandler manages download workflows for both paid and free sequences.
 * 
 * For paid sequences:
 * - Validates purchase using usePurchaseDetails hook
 * - Requires authentication
 * - Shows post-purchase confirmation
 * 
 * For free sequences:
 * - No purchase validation required
 * - Optional authentication (can download without signing in)
 * - Optional donation modal for charity sequences
 * 
 * Common features:
 * - Auto-download functionality
 * - Progress tracking
 * - Modal progression (Added to Display → Confirmation)
 * - Error handling and recovery
 */
export const GenericDownloadHandler: React.FC<GenericDownloadHandlerProps> = ({
  sequenceId,
  onDownloadComplete,
  onError,
  autoStartDownload = true,
  flowType,
  requirePurchaseValidation = flowType === 'paid',
  sequence,
  showDonationModal = false,
  showAddedToDisplayModal = true,
  showConfirmationPage = true
}) => {
  const { user, isAuthenticated } = useAuth();
  const { 
    downloadProgress, 
    modalState,
    currentSession,
    resetDownloadProgress,
    showAddedToDisplayModal: triggerAddedToDisplayModal,
    showConfirmationPage: triggerConfirmationPage,
    shouldShowAddedToDisplayModal,
    getNextModalInFlow
  } = useDownloadFlowStore();

  const [handlerState, setHandlerState] = useState<HandlerState>('initializing');
  const [hasTriggeredDownload, setHasTriggeredDownload] = useState(false);
  const [currentError, setCurrentError] = useState<Error | string | null>(null);
  const [showNetworkStatus, setShowNetworkStatus] = useState(false);
  const [networkRetryAttempt, setNetworkRetryAttempt] = useState(0);
  const [downloadSpeed, setDownloadSpeed] = useState<number | null>(null);
  
  // Network recovery manager reference
  const networkRecoveryRef = useRef<NetworkRecoveryManager | null>(null);

  // Network status monitoring
  const { networkStatus, diagnostics, isMonitoring, startMonitoring } = useNetworkStatus({
    enableDiagnostics: true,
    monitoringEnabled: true,
    diagnosticsInterval: 30000
  });

  // For paid sequences - use purchase validation
  const { purchase, isLoading: isPurchaseLoading, handleDownload: handlePaidDownload } = usePurchaseDetails(
    requirePurchaseValidation ? sequenceId : undefined
  );

  // For free sequences - use sequence purchase hook for download functionality
  const { handleDownload: handleFreeDownload, isLoading: isFreeDownloadLoading } = useSequencePurchase(
    !requirePurchaseValidation ? sequenceId : undefined
  );

  // Determine which download handler to use
  const handleDownload = requirePurchaseValidation ? handlePaidDownload : handleFreeDownload;
  const isDownloadLoading = requirePurchaseValidation ? isPurchaseLoading : isFreeDownloadLoading;

  // Determine sequence data source
  const sequenceData = requirePurchaseValidation ? purchase?.sequence : sequence;

  useEffect(() => {
    if (flowType === 'paid' && !requirePurchaseValidation) {
      console.warn('GenericDownloadHandler: Paid flow should require purchase validation');
    }
    
    if (flowType === 'free' && requirePurchaseValidation) {
      console.warn('GenericDownloadHandler: Free flow should not require purchase validation');
    }
  }, [flowType, requirePurchaseValidation]);

  // Initialize handler state
  useEffect(() => {
    if (requirePurchaseValidation) {
      // Paid sequence flow
      if (isPurchaseLoading) {
        setHandlerState('validating-access');
      } else if (!purchase) {
        setHandlerState('error');
        onError?.('Purchase validation failed: No valid purchase found');
      } else if (!isAuthenticated) {
        setHandlerState('auth-required');
      } else {
        setHandlerState('ready-to-download');
      }
    } else {
      // Free sequence flow
      if (!sequence) {
        setHandlerState('error');
        onError?.('Free sequence flow requires sequence data');
      } else if (!isAuthenticated) {
        // For free sequences, auth is optional
        setHandlerState('ready-to-download');
      } else {
        setHandlerState('ready-to-download');
      }
    }
  }, [requirePurchaseValidation, isPurchaseLoading, purchase, isAuthenticated, sequence, onError]);

  // Auto-start download when ready
  useEffect(() => {
    if (
      handlerState === 'ready-to-download' && 
      autoStartDownload && 
      !hasTriggeredDownload &&
      sequenceData
    ) {
      setHasTriggeredDownload(true);
      initiateDownload();
    }
  }, [handlerState, autoStartDownload, hasTriggeredDownload, sequenceData]);

  // Track download progress
  useEffect(() => {
    if (downloadProgress.isGeneratingUrl) {
      setHandlerState('download-starting');
    } else if (downloadProgress.isDownloading) {
      setHandlerState('downloading');
    } else if (downloadProgress.error) {
      setHandlerState('error');
      setCurrentError(downloadProgress.error);
      onError?.(downloadProgress.error);
    } else if (downloadProgress.downloadUrl && handlerState === 'downloading') {
      setHandlerState('download-complete');
    }
  }, [downloadProgress, handlerState, onError]);

  // Handle download completion and modal progression
  useEffect(() => {
    if (handlerState === 'download-complete') {
      setHandlerState('showing-modals');
      
      setTimeout(() => {
        onDownloadComplete?.();
        
        // Determine next modal in sequence
        if (showAddedToDisplayModal && shouldShowAddedToDisplayModal()) {
          triggerAddedToDisplayModal();
        } else if (showConfirmationPage) {
          const nextModal = getNextModalInFlow();
          if (nextModal === 'confirmation') {
            triggerConfirmationPage();
          }
        }
        
        setHandlerState('completed');
      }, 1000); // Brief delay to show completion message
    }
  }, [handlerState, onDownloadComplete, showAddedToDisplayModal, showConfirmationPage, shouldShowAddedToDisplayModal, triggerAddedToDisplayModal, triggerConfirmationPage, getNextModalInFlow]);

  // Initialize network recovery manager
  useEffect(() => {
    if (!networkRecoveryRef.current) {
      networkRecoveryRef.current = new NetworkRecoveryManager({
        retryConfig: {
          maxRetries: 3,
          baseDelay: 1000,
          maxDelay: 30000,
          exponentialBackoff: true,
          retryOnNetworkError: true,
          timeoutMs: 30000
        },
        enableResumption: true,
        progressCallback: (progress) => {
          setDownloadSpeed(progress.speed || null);
        },
        networkStatusCallback: (status) => {
          // Network status updates handled by useNetworkStatus hook
        },
        onNetworkRecovered: () => {
          toast.success('Connection restored! Resuming download...');
          setShowNetworkStatus(false);
          if (handlerState === 'error' && currentError && 
              (currentError.toString().includes('network') || currentError.toString().includes('offline'))) {
            retryDownload();
          }
        },
        onNetworkLost: () => {
          toast.error('Connection lost. Download will resume when connection is restored.');
          setShowNetworkStatus(true);
        }
      });
    }
  }, []);

  // Monitor network status changes
  useEffect(() => {
    if (!networkStatus.isOnline && handlerState === 'downloading') {
      setShowNetworkStatus(true);
      toast.warning('Connection lost. Download will resume automatically when connection is restored.');
    } else if (networkStatus.isOnline && showNetworkStatus) {
      setShowNetworkStatus(false);
      if (handlerState === 'error' && currentError && 
          (currentError.toString().includes('network') || currentError.toString().includes('offline'))) {
        toast.success('Connection restored! Attempting to resume download...');
        setNetworkRetryAttempt(prev => prev + 1);
        setTimeout(() => retryDownload(), 1000);
      }
    }
  }, [networkStatus.isOnline, handlerState, showNetworkStatus, currentError]);

  // Show network status monitor for poor connections
  useEffect(() => {
    if (networkStatus.connectionQuality === 'poor' && 
        (handlerState === 'downloading' || handlerState === 'download-starting')) {
      setShowNetworkStatus(true);
    } else if (networkStatus.connectionQuality === 'excellent' || networkStatus.connectionQuality === 'good') {
      if (showNetworkStatus && handlerState !== 'error') {
        setShowNetworkStatus(false);
      }
    }
  }, [networkStatus.connectionQuality, handlerState, showNetworkStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetDownloadProgress();
      networkRecoveryRef.current = null;
    };
  }, [resetDownloadProgress]);

  const initiateDownload = async () => {
    try {
      setHandlerState('download-starting');
      
      // Check network status before starting download
      if (!networkStatus.isOnline) {
        throw new Error('No internet connection. Please check your network and try again.');
      }
      
      if (networkStatus.connectionQuality === 'poor') {
        toast.warning('Poor connection detected. Download may take longer than usual.');
        setShowNetworkStatus(true);
      }
      
      // Use network recovery manager for enhanced download handling
      if (networkRecoveryRef.current) {
        await networkRecoveryRef.current.executeWithRetry(async () => {
          await handleDownload();
        });
      } else {
        await handleDownload();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const fullError = flowType === 'paid' ? `Purchase download failed: ${errorMessage}` : `Free download failed: ${errorMessage}`;
      setCurrentError(error instanceof Error ? error : new Error(fullError));
      setHandlerState('error');
      onError?.(fullError);
      
      // Show network status for network-related errors
      if (errorMessage.toLowerCase().includes('network') || 
          errorMessage.toLowerCase().includes('offline') ||
          errorMessage.toLowerCase().includes('timeout')) {
        setShowNetworkStatus(true);
      }
    }
  };

  const retryDownload = () => {
    setHasTriggeredDownload(false);
    setHandlerState('ready-to-download');
    resetDownloadProgress();
    setCurrentError(null);
  };

  // Render different states
  if (handlerState === 'validating-access') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" data-testid="loading-spinner" />
          </div>
          <CardTitle className="text-lg">
            {flowType === 'paid' ? 'Validating purchase...' : 'Preparing download...'}
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (handlerState === 'auth-required') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <User className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-lg">Authentication Required</CardTitle>
          <CardDescription>
            {flowType === 'paid' 
              ? 'Please sign in to access your purchase and start the download.'
              : 'Please sign in to download this sequence.'
            }
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (handlerState === 'error') {
    return (
      <DownloadErrorNotification
        error={currentError}
        context={{
          userId: user?.id,
          sequenceId: sequenceData?.id || sequenceId,
          sequenceTitle: sequenceData?.title,
          userAgent: navigator.userAgent,
          connectionQuality: networkStatus.connectionQuality,
          downloadSpeed: downloadSpeed,
          networkRetryAttempt,
          isOnline: networkStatus.isOnline,
          effectiveType: networkStatus.effectiveType,
          additionalContext: {
            flowType,
            requirePurchaseValidation,
            isAuthenticated,
            networkDiagnostics: diagnostics,
            networkStatus: networkStatus
          }
        }}
        onRetry={retryDownload}
        onDismiss={() => setHandlerState('ready-to-download')}
        isVisible={true}
        showToast={false}
      />
    );
  }

  if (handlerState === 'download-starting' || downloadProgress.isGeneratingUrl) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
          <CardTitle className="text-lg">Preparing download...</CardTitle>
          <CardDescription>
            Setting up your {sequenceData?.title || 'sequence'} download
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (handlerState === 'downloading' || downloadProgress.isDownloading) {
    return (
      <div className="w-full max-w-md mx-auto space-y-4">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              {!networkStatus.isOnline ? 
                <WifiOff className="h-8 w-8 text-red-600" /> : 
                <Download className="h-8 w-8 text-blue-600" />
              }
            </div>
            <CardTitle className="text-lg">
              {!networkStatus.isOnline ? 'Connection Lost' : 'Downloading...'}
            </CardTitle>
            <CardDescription>
              {sequenceData?.title} by {sequenceData?.artist}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Progress value={downloadProgress.progress} className="w-full" aria-label="Download progress" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{downloadProgress.progress}% complete</span>
                {downloadSpeed && (
                  <span>{(downloadSpeed / 1024).toFixed(1)} KB/s</span>
                )}
              </div>
              {!networkStatus.isOnline && (
                <div className="text-center text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                  Download paused. Will resume when connection is restored.
                </div>
              )}
              {networkStatus.connectionQuality === 'poor' && networkStatus.isOnline && (
                <div className="text-center text-sm text-amber-600 bg-amber-50 p-2 rounded">
                  Slow connection detected. Download may take longer than usual.
                </div>
              )}
              {networkRetryAttempt > 0 && (
                <div className="text-center text-sm text-blue-600 bg-blue-50 p-2 rounded">
                  Retry attempt {networkRetryAttempt}/3
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {showNetworkStatus && (
          <NetworkStatusMonitor 
            showDiagnostics={networkStatus.connectionQuality === 'poor' || !networkStatus.isOnline}
            compact={false}
            onNetworkRecovered={() => {
              setShowNetworkStatus(false);
              if (handlerState === 'downloading' && !networkStatus.isOnline) {
                toast.success('Connection restored! Resuming download...');
              }
            }}
            onNetworkLost={() => {
              setShowNetworkStatus(true);
              toast.warning('Connection lost. Download will resume automatically.');
            }}
            className="border-amber-200 bg-amber-50"
          />
        )}
      </div>
    );
  }

  if (handlerState === 'download-complete' || handlerState === 'showing-modals') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-lg text-green-600">Download Complete!</CardTitle>
          <CardDescription>
            {sequenceData?.title} has been successfully downloaded
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Default state - ready to download (manual)
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <CardTitle className="text-lg">
          {flowType === 'paid' ? 'Purchase Confirmed!' : 'Ready to Download'}
        </CardTitle>
        <CardDescription>
          {sequenceData?.title} by {sequenceData?.artist}
          {flowType === 'paid' && ` - $${sequenceData?.sequence_price?.toFixed(2)}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <Button 
          onClick={initiateDownload} 
          disabled={isDownloadLoading}
          className="w-full"
        >
          {isDownloadLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Start Download
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}; 